import {RefObject, useEffect, useLayoutEffect, useRef} from "react";
import {css} from "@panda/css";
import {useReducedMotion} from "framer-motion";

import {Button} from "@pg-design/button-module";
import {CloseIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {KM_HM_TEST_IDS} from "@pg-mono/test-ids";

import {ResponsiveOverlay} from "@/components/responsive_overlay";
import {zIndexes} from "@/features/km_app/constants/z_indexes";
import {unstyledButton} from "@/styles/receipes/unstyled_button";

import {HOME_MATCH_PREFERENCES_WIZARD_CONTENT} from "../../constants/preferences/home_match_preferences_wizard_content";

const {DESCRIPTION, RETURN, SAVE, TITLE} = HOME_MATCH_PREFERENCES_WIZARD_CONTENT.EXIT;

interface IProps {
    description?: string;
    isSaveDisabled?: boolean;
    onReturn: () => void;
    onSave: () => void;
    returnLabel?: string;
    saveLabel?: string;
    title?: string;
}

interface ILayerProps extends IProps {
    onDismiss: () => void;
}

interface ISheetProps extends ILayerProps {
    isOpen: boolean;
    zIndex?: number;
}

export const HomeMatchPreferencesWizardExitSheet = (props: ISheetProps) => {
    const {
        description = DESCRIPTION,
        isOpen,
        isSaveDisabled,
        onDismiss,
        onReturn,
        onSave,
        returnLabel,
        saveLabel,
        title = TITLE,
        zIndex = zIndexes.nestedModal
    } = props;

    return (
        <ResponsiveOverlay
            ariaLabel={title}
            dataTestId={KM_HM_TEST_IDS.PREFERENCES_WIZARD_EXIT}
            footer={<ExitActions isSaveDisabled={isSaveDisabled} onReturn={onReturn} onSave={onSave} returnLabel={returnLabel} saveLabel={saveLabel} stacked />}
            isOpen={isOpen}
            onClose={onDismiss}
            showCloseButton={false}
            zIndex={zIndex}
        >
            <div className={sheetCopyStyle}>
                <Text as="h2" color="var(--colors-secondary)" preserveFontSize variant="headline_4">
                    {title}
                </Text>
                <Text color="var(--colors-gray-700)" preserveFontSize variant="body_copy_2">
                    {description}
                </Text>
            </div>
        </ResponsiveOverlay>
    );
};

export const HomeMatchPreferencesWizardExitLayer = (props: ILayerProps) => {
    const {description, isSaveDisabled, onDismiss, onReturn, onSave, returnLabel, saveLabel, title} = props;
    const panelRef = useRef<HTMLDivElement>(null);
    const shouldReduceMotion = useReducedMotion();

    useLayoutEffect(() => {
        const panel = panelRef.current;

        if (!panel || shouldReduceMotion || typeof panel.animate !== "function") return;

        panel.animate(
            [
                {opacity: 0, transform: "translateY(0.8rem)"},
                {opacity: 1, transform: "translateY(0)"}
            ],
            {
                duration: 200,
                easing: "cubic-bezier(0.4, 0, 0.2, 1)",
                fill: "backwards"
            }
        );
    }, [shouldReduceMotion]);

    return (
        <div className={panelStyle} data-testid={KM_HM_TEST_IDS.PREFERENCES_WIZARD_EXIT} ref={panelRef}>
            <button aria-label="Zamknij" className={closeButtonStyle} onClick={onDismiss} type="button">
                <CloseIcon fill="var(--colors-secondary)" size="2.4" wrapperColor="transparent" wrapperSize="2.4" />
            </button>

            <HomeMatchPreferencesWizardExit
                description={description}
                isSaveDisabled={isSaveDisabled}
                onReturn={onReturn}
                onSave={onSave}
                returnLabel={returnLabel}
                saveLabel={saveLabel}
                title={title}
            />
        </div>
    );
};

export const HomeMatchPreferencesWizardExit = (props: IProps) => {
    const {description = DESCRIPTION, isSaveDisabled = false, onReturn, onSave, returnLabel, saveLabel, title = TITLE} = props;
    const returnButtonHolderRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        returnButtonHolderRef.current?.querySelector("button")?.focus();
    }, []);

    return (
        <div className={layoutStyle}>
            <div className={headerStyle} />
            <div className={contentStyle}>
                <Text as="h2" color="var(--colors-secondary)" preserveFontSize variant="headline_4">
                    {title}
                </Text>
                <Text color="var(--colors-gray-700)" preserveFontSize variant="body_copy_2">
                    {description}
                </Text>
            </div>
            <ExitActions
                holderRef={returnButtonHolderRef}
                isSaveDisabled={isSaveDisabled}
                onReturn={onReturn}
                onSave={onSave}
                returnLabel={returnLabel}
                saveLabel={saveLabel}
            />
        </div>
    );
};

interface IExitActionsProps extends IProps {
    holderRef?: RefObject<HTMLDivElement | null>;
    stacked?: boolean;
}

const ExitActions = (props: IExitActionsProps) => {
    const {holderRef, isSaveDisabled = false, onReturn, onSave, returnLabel = RETURN, saveLabel = SAVE, stacked = false} = props;

    return (
        <div className={stacked ? sheetActionsStyle : actionsStyle}>
            <Button
                className={stacked ? undefined : saveButtonStyle}
                dataTestId={KM_HM_TEST_IDS.PREFERENCES_WIZARD_EXIT_SAVE}
                disabled={isSaveDisabled}
                fullWidth={stacked}
                onClick={onSave}
                size="small"
                textVariant="button_medium"
                type="button"
                variant="none_secondary"
            >
                {saveLabel}
            </Button>
            <div ref={holderRef}>
                <Button
                    dataTestId={KM_HM_TEST_IDS.PREFERENCES_WIZARD_EXIT_RETURN}
                    fullWidth={stacked}
                    onClick={onReturn}
                    size="small"
                    textVariant="button_medium"
                    type="button"
                    variant="filled_secondary"
                >
                    {returnLabel}
                </Button>
            </div>
        </div>
    );
};

const panelStyle = css({
    backgroundColor: "var(--colors-white)",
    borderRadius: 2,
    boxShadow: "var(--shadow-1)",
    maxHeight: "100%",
    maxWidth: "100%",
    position: "relative",
    width: "64rem"
});

const closeButtonStyle = css(unstyledButton, {
    position: "absolute",
    right: 3,
    top: 3
});

const layoutStyle = css({
    display: "flex",
    flexDirection: "column"
});

const headerStyle = css({
    height: "7.2rem"
});

const contentStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    px: 7.5,
    py: 3
});

const sheetCopyStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2
});

const saveButtonStyle = css({
    "&:disabled": {
        borderColor: "transparent"
    }
});

const actionsStyle = css({
    display: "flex",
    height: "9.6rem",
    justifyContent: "space-between",
    px: 7.5,
    py: 3
});

const sheetActionsStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1,

    "& > button": {
        order: 1
    }
});
import {RefObject, useEffect, useLayoutEffect, useRef} from "react";
import {css} from "@panda/css";
import {useReducedMotion} from "framer-motion";

import {Button} from "@pg-design/button-module";
import {CloseIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {KM_HM_TEST_IDS} from "@pg-mono/test-ids";

import {ResponsiveOverlay} from "@/components/responsive_overlay";
import {zIndexes} from "@/features/km_app/constants/z_indexes";
import {unstyledButton} from "@/styles/receipes/unstyled_button";

import {HOME_MATCH_PREFERENCES_WIZARD_CONTENT} from "../../constants/preferences/home_match_preferences_wizard_content";

const {DESCRIPTION, RETURN, SAVE, TITLE} = HOME_MATCH_PREFERENCES_WIZARD_CONTENT.EXIT;

interface IProps {
    description?: string;
    isSaveDisabled?: boolean;
    onReturn: () => void;
    onSave: () => void;
    returnLabel?: string;
    saveLabel?: string;
    title?: string;
}

interface ILayerProps extends IProps {
    onDismiss: () => void;
}

interface ISheetProps extends ILayerProps {
    isOpen: boolean;
    zIndex?: number;
}

export const HomeMatchPreferencesWizardExitSheet = (props: ISheetProps) => {
    const {
        description = DESCRIPTION,
        isOpen,
        isSaveDisabled,
        onDismiss,
        onReturn,
        onSave,
        returnLabel,
        saveLabel,
        title = TITLE,
        zIndex = zIndexes.nestedModal
    } = props;

    return (
        <ResponsiveOverlay
            ariaLabel={title}
            dataTestId={KM_HM_TEST_IDS.PREFERENCES_WIZARD_EXIT}
            footer={<ExitActions isSaveDisabled={isSaveDisabled} onReturn={onReturn} onSave={onSave} returnLabel={returnLabel} saveLabel={saveLabel} stacked />}
            isOpen={isOpen}
            onClose={onDismiss}
            showCloseButton={false}
            zIndex={zIndex}
        >
            <div className={sheetCopyStyle}>
                <Text as="h2" color="var(--colors-secondary)" preserveFontSize variant="headline_4">
                    {title}
                </Text>
                <Text color="var(--colors-gray-700)" preserveFontSize variant="body_copy_2">
                    {description}
                </Text>
            </div>
        </ResponsiveOverlay>
    );
};

export const HomeMatchPreferencesWizardExitLayer = (props: ILayerProps) => {
    const {description, isSaveDisabled, onDismiss, onReturn, onSave, returnLabel, saveLabel, title} = props;
    const panelRef = useRef<HTMLDivElement>(null);
    const shouldReduceMotion = useReducedMotion();

    useLayoutEffect(() => {
        const panel = panelRef.current;

        if (!panel || shouldReduceMotion || typeof panel.animate !== "function") return;

        panel.animate(
            [
                {opacity: 0, transform: "translateY(0.8rem)"},
                {opacity: 1, transform: "translateY(0)"}
            ],
            {
                duration: 200,
                easing: "cubic-bezier(0.4, 0, 0.2, 1)",
                fill: "backwards"
            }
        );
    }, [shouldReduceMotion]);

    return (
        <div className={panelStyle} data-testid={KM_HM_TEST_IDS.PREFERENCES_WIZARD_EXIT} ref={panelRef}>
            <button aria-label="Zamknij" className={closeButtonStyle} onClick={onDismiss} type="button">
                <CloseIcon fill="var(--colors-secondary)" size="2.4" wrapperColor="transparent" wrapperSize="2.4" />
            </button>

            <HomeMatchPreferencesWizardExit
                description={description}
                isSaveDisabled={isSaveDisabled}
                onReturn={onReturn}
                onSave={onSave}
                returnLabel={returnLabel}
                saveLabel={saveLabel}
                title={title}
            />
        </div>
    );
};

export const HomeMatchPreferencesWizardExit = (props: IProps) => {
    const {description = DESCRIPTION, isSaveDisabled = false, onReturn, onSave, returnLabel, saveLabel, title = TITLE} = props;
    const returnButtonHolderRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        returnButtonHolderRef.current?.querySelector("button")?.focus();
    }, []);

    return (
        <div className={layoutStyle}>
            <div className={headerStyle} />
            <div className={contentStyle}>
                <Text as="h2" color="var(--colors-secondary)" preserveFontSize variant="headline_4">
                    {title}
                </Text>
                <Text color="var(--colors-gray-700)" preserveFontSize variant="body_copy_2">
                    {description}
                </Text>
            </div>
            <ExitActions
                holderRef={returnButtonHolderRef}
                isSaveDisabled={isSaveDisabled}
                onReturn={onReturn}
                onSave={onSave}
                returnLabel={returnLabel}
                saveLabel={saveLabel}
            />
        </div>
    );
};

interface IExitActionsProps extends IProps {
    holderRef?: RefObject<HTMLDivElement | null>;
    stacked?: boolean;
}

const ExitActions = (props: IExitActionsProps) => {
    const {holderRef, isSaveDisabled = false, onReturn, onSave, returnLabel = RETURN, saveLabel = SAVE, stacked = false} = props;

    return (
        <div className={stacked ? sheetActionsStyle : actionsStyle}>
            <Button
                className={stacked ? undefined : saveButtonStyle}
                dataTestId={KM_HM_TEST_IDS.PREFERENCES_WIZARD_EXIT_SAVE}
                disabled={isSaveDisabled}
                fullWidth={stacked}
                onClick={onSave}
                size="small"
                textVariant="button_medium"
                type="button"
                variant="none_secondary"
            >
                {saveLabel}
            </Button>
            <div ref={holderRef}>
                <Button
                    dataTestId={KM_HM_TEST_IDS.PREFERENCES_WIZARD_EXIT_RETURN}
                    fullWidth={stacked}
                    onClick={onReturn}
                    size="small"
                    textVariant="button_medium"
                    type="button"
                    variant="filled_secondary"
                >
                    {returnLabel}
                </Button>
            </div>
        </div>
    );
};

const panelStyle = css({
    backgroundColor: "var(--colors-white)",
    borderRadius: 2,
    boxShadow: "var(--shadow-1)",
    maxHeight: "100%",
    maxWidth: "100%",
    position: "relative",
    width: "64rem"
});

const closeButtonStyle = css(unstyledButton, {
    position: "absolute",
    right: 3,
    top: 3
});

const layoutStyle = css({
    display: "flex",
    flexDirection: "column"
});

const headerStyle = css({
    height: "7.2rem"
});

const contentStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    px: 7.5,
    py: 3
});

const sheetCopyStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2
});

const saveButtonStyle = css({
    "&:disabled": {
        borderColor: "transparent"
    }
});

const actionsStyle = css({
    display: "flex",
    height: "9.6rem",
    justifyContent: "space-between",
    px: 7.5,
    py: 3
});

const sheetActionsStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1,

    "& > button": {
        order: 1
    }
});
